import type { DerivedSnapshot } from "@/lib/derivedIntel/derivedIntelligenceEngine";

/**
 * Community Sentiment Synthesis — combines polls, reactions, hype and
 * derived signals into shareable social insights. Pure deterministic.
 *
 * Inputs are plain numbers from existing engagement layers; no new polling.
 */

export interface CommunitySignals {
  /** % of community predicting a goal in the next 5 min, 0..1 */
  goalBeliefPct?: number;
  /** Net positive reactions per minute, capped */
  reactionRatePerMin?: number;
  /** Hype index 0..1 from existing hype meter */
  hypeIndex?: number;
  /** Nation rivalry support, signed: + home, - away, -1..1 */
  nationSupportBias?: number;
  /** Total active voters in the rolling window */
  activeVoters?: number;
  /** Net % of users believing a comeback is coming, 0..1 */
  comebackBeliefPct?: number;
  /** % of users showing fear / panic (downvotes, rage reactions), 0..1 */
  panicIndex?: number;
  /** Net betting crowd lean for home (+) vs away (-), -1..1 */
  bettingCrowdBias?: number;
  /** % of users supporting the underdog emotionally, 0..1 */
  underdogSupportPct?: number;
  /** Δ hype index vs previous window, signed, -1..1 */
  hypeMomentum?: number;
}

export interface CommunityInsight {
  id: string;
  text: string;
  weight: number; // 0..1 viral weight
  /**
   * Emotional polarity of the insight.
   *  +1 = euphoric / confident, -1 = panic / collapse, 0 = neutral.
   */
  polarity?: number;
  tag?:
    | "goal_belief"
    | "def_collapse"
    | "nation_support"
    | "react_spike"
    | "comeback_belief"
    | "panic"
    | "confidence_collapse"
    | "underdog_support"
    | "betting_bias"
    | "crowd_pressure"
    | "hype_swing";
}

export interface CommunityContext {
  homeLabel: string;
  awayLabel: string;
  lang?: "ES" | "EN" | "PT";
}

export function synthesizeCommunityInsights(
  snap: DerivedSnapshot,
  signals: CommunitySignals,
  ctx: CommunityContext,
): CommunityInsight[] {
  const lang = ctx.lang ?? "ES";
  const out: CommunityInsight[] = [];
  const voters = signals.activeVoters ?? 0;

  if (signals.goalBeliefPct !== undefined && voters >= 20) {
    const p = Math.round(signals.goalBeliefPct * 100);
    out.push({
      id: "goalBelief",
      text:
        lang === "EN"
          ? `${p}% of the community believes a goal is coming.`
          : lang === "PT"
            ? `${p}% da comunidade acredita que o gol está próximo.`
            : `${p}% cree que llegará el gol.`,
      weight: signals.goalBeliefPct,
      polarity: signals.goalBeliefPct > 0.55 ? 0.6 : 0,
      tag: "goal_belief",
    });
  }
  const dr = Math.max(snap.signals.defensiveRiskHome, snap.signals.defensiveRiskAway);
  if (dr > 0.7 && (signals.hypeIndex ?? 0) > 0.5) {
    const sufferingLabel =
      snap.signals.defensiveRiskHome > snap.signals.defensiveRiskAway
        ? ctx.homeLabel
        : ctx.awayLabel;
    out.push({
      id: "defCollapse",
      text:
        lang === "EN"
          ? `The community senses a defensive collapse from ${sufferingLabel}.`
          : lang === "PT"
            ? `A comunidade detecta colapso defensivo de ${sufferingLabel}.`
            : `La comunidad detecta colapso defensivo de ${sufferingLabel}.`,
      weight: dr,
      polarity: -0.7,
      tag: "def_collapse",
    });
  }
  if (signals.nationSupportBias !== undefined && Math.abs(signals.nationSupportBias) > 0.3) {
    const label = signals.nationSupportBias > 0 ? ctx.homeLabel : ctx.awayLabel;
    out.push({
      id: "nationSupport",
      text:
        lang === "EN"
          ? `${label} is gaining emotional support.`
          : lang === "PT"
            ? `${label} ganha apoio emocional.`
            : `${label} gana apoyo emocional.`,
      weight: Math.abs(signals.nationSupportBias),
      polarity: 0.4,
      tag: "nation_support",
    });
  }
  if ((signals.reactionRatePerMin ?? 0) > 40) {
    out.push({
      id: "reactSpike",
      text:
        lang === "EN"
          ? `Reaction spike: community is on fire right now.`
          : lang === "PT"
            ? `Pico de reações: comunidade pegando fogo.`
            : `Pico de reacciones: la comunidad está encendida.`,
      weight: Math.min(1, (signals.reactionRatePerMin ?? 0) / 80),
      polarity: 0.8,
      tag: "react_spike",
    });
  }

  // Comeback belief — only meaningful with voters present.
  if (signals.comebackBeliefPct !== undefined && signals.comebackBeliefPct > 0.45 && voters >= 30) {
    out.push({
      id: "comeback",
      text:
        lang === "EN"
          ? `The crowd believes in a comeback.`
          : lang === "PT"
            ? `A torcida acredita na virada.`
            : `La afición cree en la remontada.`,
      weight: signals.comebackBeliefPct,
      polarity: 0.7,
      tag: "comeback_belief",
    });
  }

  // Panic detection.
  if ((signals.panicIndex ?? 0) > 0.55) {
    out.push({
      id: "panic",
      text:
        lang === "EN"
          ? `Panic is spreading among supporters.`
          : lang === "PT"
            ? `Pânico se espalha na torcida.`
            : `El pánico empieza a propagarse en la afición.`,
      weight: signals.panicIndex ?? 0,
      polarity: -0.9,
      tag: "panic",
    });
  }

  // Confidence collapse — hype dropping fast while pressure mounts.
  if ((signals.hypeMomentum ?? 0) < -0.35 && dr > 0.6) {
    out.push({
      id: "confCollapse",
      text:
        lang === "EN"
          ? `The community is starting to lose confidence.`
          : lang === "PT"
            ? `A comunidade começa a perder a confiança.`
            : `La comunidad empieza a perder confianza.`,
      weight: Math.min(1, Math.abs(signals.hypeMomentum ?? 0) + 0.2),
      polarity: -0.6,
      tag: "confidence_collapse",
    });
  }

  // Underdog support.
  if ((signals.underdogSupportPct ?? 0) > 0.55) {
    out.push({
      id: "underdogSupport",
      text:
        lang === "EN"
          ? `Neutrals are rallying behind the underdog.`
          : lang === "PT"
            ? `Os neutros torcem pelo azarão.`
            : `Los neutrales se vuelcan con el favorito moral.`,
      weight: signals.underdogSupportPct ?? 0,
      polarity: 0.5,
      tag: "underdog_support",
    });
  }

  // Betting crowd bias.
  if (signals.bettingCrowdBias !== undefined && Math.abs(signals.bettingCrowdBias) > 0.35) {
    const label = signals.bettingCrowdBias > 0 ? ctx.homeLabel : ctx.awayLabel;
    out.push({
      id: "bettingBias",
      text:
        lang === "EN"
          ? `The betting crowd is leaning hard on ${label}.`
          : lang === "PT"
            ? `O público apostador inclina-se forte por ${label}.`
            : `El público apostador se inclina fuerte por ${label}.`,
      weight: Math.abs(signals.bettingCrowdBias),
      polarity: 0.2,
      tag: "betting_bias",
    });
  }

  // Crowd pressure — high hype + extreme pressure on field.
  if ((signals.hypeIndex ?? 0) > 0.7 && snap.signals.pressureExtreme) {
    out.push({
      id: "crowdPressure",
      text:
        lang === "EN"
          ? `Collective tension is rising.`
          : lang === "PT"
            ? `A tensão coletiva aumenta.`
            : `La tensión colectiva aumenta.`,
      weight: Math.min(1, (signals.hypeIndex ?? 0) + 0.1),
      polarity: 0.3,
      tag: "crowd_pressure",
    });
  }

  // Hype momentum swing.
  if (signals.hypeMomentum !== undefined && Math.abs(signals.hypeMomentum) > 0.45) {
    out.push({
      id: "hypeSwing",
      text:
        lang === "EN"
          ? signals.hypeMomentum > 0
            ? `Emotional momentum is swinging up.`
            : `Emotional momentum is collapsing.`
          : lang === "PT"
            ? signals.hypeMomentum > 0
              ? `O ânimo coletivo dispara.`
              : `O ânimo coletivo despenca.`
            : signals.hypeMomentum > 0
              ? `El ánimo colectivo se dispara.`
              : `El ánimo colectivo se desploma.`,
      weight: Math.min(1, Math.abs(signals.hypeMomentum)),
      polarity: signals.hypeMomentum > 0 ? 0.5 : -0.5,
      tag: "hype_swing",
    });
  }

  return out.sort((a, b) => b.weight - a.weight);
}

/**
 * Aggregate emotional polarity across all current insights, -1..1.
 * Useful for downstream coordinators (intensity arbiter, story arc).
 */
export function aggregateEmotionalPolarity(insights: CommunityInsight[]): number {
  if (insights.length === 0) return 0;
  let sum = 0;
  let wTotal = 0;
  for (const i of insights) {
    const p = i.polarity ?? 0;
    sum += p * i.weight;
    wTotal += i.weight;
  }
  if (wTotal === 0) return 0;
  return Math.max(-1, Math.min(1, sum / wTotal));
}